export type Owner = 'Scott' | 'BAL' | 'HR';
export type PhaseName = 'Decision' | 'Execution';

export type Segment = {
    id: string;
    phase: PhaseName;
    owner: Owner;
    start: string;
    end: string;
    title: string;
    evidence: string;
    estimated?: boolean;
};

export type Followup = {
    number: number;
    phase: PhaseName;
    lane: 0 | 1;
    at: string;
    title: string;
    evidence: string;
    kind: 'Status follow-up' | 'Urgency prompt';
};

export type ProcessStep = {
    number: number;
    status: 'complete' | 'current' | 'pending';
    title: string;
    detail: string;
};

export const ownerLabels: Record<Owner, string> = {
    Scott: 'Scott',
    BAL: 'BAL',
    HR: 'Datadog HR',
};

export const segments: Segment[] = [
    {
        id: 'strategy-review',
        phase: 'Decision',
        owner: 'BAL',
        start: '2026-04-24T18:19:00-04:00',
        end: '2026-06-19T19:46:00-04:00',
        title: 'Determine whether H-1B or TN was the appropriate strategy',
        evidence:
            'Scott explicitly asked on April 24 whether BAL still planned to switch him back to H-1B. BAL promised to review the file and discuss internally. BAL did not state that it would approach HR about H-1B instead of TN until June 19.',
    },
    {
        id: 'coordinate-authorization',
        phase: 'Decision',
        owner: 'BAL',
        start: '2026-06-19T19:46:00-04:00',
        end: '2026-06-25T12:00:00-04:00',
        title: 'Seek and coordinate HR authorization',
        evidence:
            'BAL said on June 19 that it would reach out to HR the following week. The emails establish that the HR meeting occurred June 25; noon is used as a neutral estimated boundary.',
        estimated: true,
    },
    {
        id: 'hr-authorization',
        phase: 'Decision',
        owner: 'HR',
        start: '2026-06-25T12:00:00-04:00',
        end: '2026-07-07T12:08:00-04:00',
        title: 'HR authorization pending',
        evidence:
            'BAL said on June 26 that it was awaiting HR confirmation. BAL communicated approval to proceed on July 7. This entire gap is conservatively assigned to HR.',
        estimated: true,
    },
    {
        id: 'open-case',
        phase: 'Execution',
        owner: 'BAL',
        start: '2026-07-07T12:08:00-04:00',
        end: '2026-07-07T14:13:00-04:00',
        title: 'Open H-1B case and assign questionnaire',
        evidence:
            'BAL communicated HR approval at 12:08 PM and opened the H-1B case and questionnaire by 2:13 PM.',
    },
    {
        id: 'questionnaire',
        phase: 'Execution',
        owner: 'Scott',
        start: '2026-07-07T14:13:00-04:00',
        end: '2026-07-07T17:20:00-04:00',
        title: 'Complete H-1B questionnaire',
        evidence:
            'Scott confirmed the H-1B questionnaire was complete 3 hours and 7 minutes after it was assigned.',
    },
    {
        id: 'petition-preparation',
        phase: 'Execution',
        owner: 'BAL',
        start: '2026-07-07T17:20:00-04:00',
        end: '2026-07-14T16:28:00-04:00',
        title: 'Prepare H-1B and LCA',
        evidence:
            'BAL first estimated about three weeks, then four to six weeks. After escalation, BAL committed to submit the LCA by July 15 and file the H-1B during the week of July 27.',
    },
    {
        id: 'documents',
        phase: 'Execution',
        owner: 'Scott',
        start: '2026-07-14T16:28:00-04:00',
        end: '2026-07-14T18:22:00-04:00',
        title: 'Upload newly requested documents',
        evidence:
            'After BAL requested the new passport and two months of pay statements, Cobalt records show the final requested or related upload at 6:22 PM.',
    },
    {
        id: 'attorney-review',
        phase: 'Execution',
        owner: 'BAL',
        start: '2026-07-14T18:22:00-04:00',
        end: '2026-07-15T09:18:33-04:00',
        title: 'LCA attorney review; submission still unconfirmed',
        evidence:
            'At 9:18 AM on July 15, BAL said the LCA was with the attorney for review and would be submitted after review and portal posting. The H-1B petition remained unfiled.',
    },
];

export const followups: Followup[] = [
    {
        number: 1,
        phase: 'Decision',
        lane: 0,
        at: '2026-06-04T17:55:00-04:00',
        title: 'Re-raised the unresolved TN versus H-1B strategy',
        evidence:
            'After completing the TN questionnaire, Scott again flagged that the legal strategy was still unresolved and explained that travel during the remaining green-card steps mattered.',
        kind: 'Urgency prompt',
    },
    {
        number: 2,
        phase: 'Decision',
        lane: 1,
        at: '2026-06-16T09:46:00-04:00',
        title: 'Asked for status and next steps',
        evidence:
            'Scott wrote, “Just following up. Do we know what next steps are?” and tied the request to mid-August travel planning.',
        kind: 'Status follow-up',
    },
    {
        number: 3,
        phase: 'Decision',
        lane: 0,
        at: '2026-06-23T09:39:00-04:00',
        title: 'Asked again for PERM, H-1B, or TN next steps',
        evidence:
            'Scott confirmed the long-form birth certificate upload and asked what came next across the green-card and visa tracks.',
        kind: 'Status follow-up',
    },
    {
        number: 4,
        phase: 'Decision',
        lane: 1,
        at: '2026-06-26T11:08:00-04:00',
        title: 'Asked for the outcome of the HR meeting',
        evidence:
            'Scott followed up after BAL had said it would update the thread following its discussion with Datadog HR.',
        kind: 'Status follow-up',
    },
    {
        number: 5,
        phase: 'Decision',
        lane: 0,
        at: '2026-07-06T10:20:00-04:00',
        title: 'Restated the unresolved status and asked for next steps',
        evidence:
            'Scott summarized that BAL appeared to be waiting for HR approval, that he had been told not to book travel, and that he needed a path for attending the August 29 wedding.',
        kind: 'Status follow-up',
    },
    {
        number: 6,
        phase: 'Execution',
        lane: 0,
        at: '2026-07-07T17:20:00-04:00',
        title: 'Completed the questionnaire and asked BAL to prioritize filing',
        evidence:
            'Scott completed the H-1B questionnaire the same day it was assigned and immediately asked whether the filing could be prioritized.',
        kind: 'Urgency prompt',
    },
    {
        number: 7,
        phase: 'Execution',
        lane: 1,
        at: '2026-07-10T12:15:00-04:00',
        title: 'Asked whether the H-1B was on track',
        evidence:
            'Scott asked for green-card next steps and whether the H-1B process was still trending toward readiness by July 28.',
        kind: 'Status follow-up',
    },
    {
        number: 8,
        phase: 'Execution',
        lane: 0,
        at: '2026-07-14T10:39:00-04:00',
        title: 'Escalated for a concrete roadmap and dates',
        evidence:
            'Scott requested a target filing date, premium-processing confirmation, remaining steps, acceleration measures, and a contingency if filing or approval approached September 8.',
        kind: 'Status follow-up',
    },
    {
        number: 9,
        phase: 'Execution',
        lane: 1,
        at: '2026-07-15T09:09:00-04:00',
        title: 'Pressed the stated LCA commitment',
        evidence:
            'Scott cited BAL’s commitment to submit the LCA by July 15 and said he expected submission before the scheduled meeting.',
        kind: 'Urgency prompt',
    },
];

export const processSteps: ProcessStep[] = [
    {
        number: 1,
        status: 'complete',
        title: 'Select H-1B over TN',
        detail: 'Legal strategy resolved after 56.1 days',
    },
    {
        number: 2,
        status: 'complete',
        title: 'Obtain HR approval',
        detail: 'Approval communicated July 7',
    },
    {
        number: 3,
        status: 'complete',
        title: 'H-1B questionnaire',
        detail: 'Scott completed in 3h 7m',
    },
    {
        number: 4,
        status: 'complete',
        title: 'Requested documents',
        detail: 'Scott uploaded in 1h 54m',
    },
    {
        number: 5,
        status: 'current',
        title: 'Attorney LCA review',
        detail: 'Submission not yet confirmed',
    },
    {
        number: 6,
        status: 'pending',
        title: 'Submit LCA',
        detail: 'BAL committed to July 15',
    },
    {
        number: 7,
        status: 'pending',
        title: 'LCA certification',
        detail: 'BAL estimated 7–10 days',
    },
    {
        number: 8,
        status: 'pending',
        title: 'File H-1B',
        detail: 'Committed week of July 27',
    },
    {
        number: 9,
        status: 'pending',
        title: 'Premium processing',
        detail: '15 business-day USCIS action period',
    },
];

export const phases: PhaseName[] = ['Decision', 'Execution'];

export const phaseCopy: Record<
    PhaseName,
    { title: string; dates: string; takeaway: string }
> = {
    Decision: {
        title: '1 · Decide between TN and H-1B',
        dates: 'Apr 24 → Jul 7 · 73.7 calendar days',
        takeaway:
            'BAL held 61.7 days to determine the strategy and coordinate authorization; HR held the final 12.0-day approval gap.',
    },
    Execution: {
        title: '2 · Prepare and file the H-1B',
        dates: 'Jul 7 → Jul 15 · 7.9 calendar days and counting',
        takeaway:
            'Scott’s H-1B questionnaire and document turnarounds totaled about five hours. BAL held the remaining 97.3% of the execution clock.',
    },
};
